import '../../../utils/leafletGlobal';
import 'leaflet-rotatedmarker';

import L from 'leaflet';
import { memo } from 'react';
import { useEffect, useRef } from 'react';
import { Marker, Popup } from 'react-leaflet';

import { IVehicle } from '../../../schema/Vehicle.ts';
import { VehicleBadge } from '../../ui/Badge.tsx';

type VehicleVariant = 'selected' | 'in-operation' | 'inactive';

const vehicle_in_operation = new L.Icon({
  iconUrl: '/theme/media/leaflet_padam_icons/location-green.svg',
  iconSize: [60, 60],
  popupAnchor: [0, -20],
  iconAnchor: [30, 30],
});

const vehicle_selected_icon = new L.Icon({
  iconUrl: '/theme/media/leaflet_padam_icons/location-green-select.svg',
  iconSize: [60, 60],
  popupAnchor: [0, -20],
  iconAnchor: [30, 30],
});

const vehicle_inactive_icon = new L.Icon({
  iconUrl: '/theme/media/leaflet_padam_icons/location-gray.svg',
  iconSize: [60, 60],
  popupAnchor: [0, -20],
  iconAnchor: [30, 30],
});

const vehicleIcons: Record<VehicleVariant, L.Icon> = {
  selected: vehicle_selected_icon,
  'in-operation': vehicle_in_operation,
  inactive: vehicle_inactive_icon,
};

// Leaflet stacks markers by screen y + zIndexOffset; steps of 10000 exceed any viewport height,
// so variant order always wins: selected > in-operation > inactive.
const vehicleZIndexOffsets: Record<VehicleVariant, number> = {
  selected: 30000,
  'in-operation': 20000,
  inactive: 10000,
};

type VehicleMarkerProps = {
  vehicle: IVehicle & { latitude: number; longitude: number };
  variant: VehicleVariant;
  rotationAngle?: number;
};

export const VehicleMarker = memo(function VehicleMarker({
  vehicle,
  variant,
  rotationAngle = 0,
}: Readonly<VehicleMarkerProps>) {
  const markerRef = useRef<L.Marker>(null);

  // react-leaflet's Marker only re-syncs position/icon/zIndexOffset/opacity/draggable
  // on prop changes, so rotationAngle updates have to go through the plugin's API directly.
  useEffect(() => {
    markerRef.current?.setRotationAngle(rotationAngle);
  }, [rotationAngle]);

  return (
    <Marker
      ref={markerRef}
      icon={vehicleIcons[variant]}
      rotationAngle={rotationAngle}
      position={[vehicle.latitude, vehicle.longitude]}
      zIndexOffset={vehicleZIndexOffsets[variant]}>
      <Popup>
        <div className="d-flex align-items-center gap-2">
          <VehicleBadge vehicle={vehicle} />
          <span className={'card-text'}>{vehicle.display_identifier}</span>
        </div>
        <div className={'mt-2'}>
          <span className={'text-basic'}>
            ({vehicle.latitude.toFixed(4)}; {vehicle.longitude.toFixed(4)})
          </span>
        </div>
        <div className={'text-basic mt-2'}>
          <span className={'text-basic'}>{vehicle.next_node}</span>
        </div>
      </Popup>
    </Marker>
  );
});
